import { useState, type FormEvent } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { ApiError } from '@/api/errors'
import { Button } from '@/components/ui/Button'
import { PasswordInput, MIN_PASSWORD_LENGTH, scorePassword } from '@/components/ui/PasswordInput'
import { AlertTriangle, ICON_SIZE, ICON_STROKE } from '@/components/icons'

/**
 * Password-change form with callback on success for reuse.
 */
export function ChangePasswordForm({
  currentPasswordLabel = 'Current password',
  submitLabel = 'Change password',
  onSuccess,
}: {
  currentPasswordLabel?: string
  submitLabel?: string
  onSuccess: () => void
}) {
  const { changePassword } = useAuth()

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const { strength, meets } = scorePassword(next)
  const matches = confirm.length > 0 && next === confirm
  const differs = next.length > 0 && next !== current
  const canSubmit =
    current.length > 0 && meets.length && meets.notCommon && matches && differs && !submitting

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      await changePassword(current, next)
      onSuccess()
    } catch (err) {
      setError(messageFor(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={(e) => { void onSubmit(e) }} className="flex flex-col gap-4" noValidate>
      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-danger-border bg-danger-bg px-3 py-2.5 text-sm text-danger"
        >
          <AlertTriangle size={ICON_SIZE.sm} strokeWidth={ICON_STROKE} aria-hidden className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <PasswordInput
        label={currentPasswordLabel}
        autoComplete="current-password"
        required
        value={current}
        onChange={(e) => { setCurrent(e.target.value) }}
        disabled={submitting}
      />

      <PasswordInput
        label="New password"
        autoComplete="new-password"
        required
        showStrength
        value={next}
        onChange={(e) => { setNext(e.target.value) }}
        disabled={submitting}
        // Shown only once there is something to be wrong about — a rule that
        // turns red on the first keystroke is hostile, not helpful.
        error={next.length > 0 && !differs ? 'Choose a password different from your current one.' : null}
      />

      <PasswordInput
        label="Confirm new password"
        autoComplete="new-password"
        required
        value={confirm}
        onChange={(e) => { setConfirm(e.target.value) }}
        disabled={submitting}
        error={confirm.length > 0 && !matches ? 'These passwords do not match.' : null}
      />

      <Button type="submit" variant="primary" fullWidth loading={submitting} disabled={!canSubmit}>
        {submitLabel}
      </Button>

      <p className="text-xs text-subtle">
        At least {MIN_PASSWORD_LENGTH} characters. Longer beats more complicated — a memorable
        phrase is stronger than a short password with symbols in it.
        {strength === 'strong' ? ' This one is strong.' : ''}
      </p>
    </form>
  )
}

function messageFor(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Something went wrong. Please try again.'
  if (error.code === 'WEAK_PASSWORD') {
    return error.message || 'That password is too easy to guess. Try a longer phrase.'
  }
  // Unlike the login form, the user is already authenticated here, so there is
  // no account to enumerate — say plainly that the current password was wrong.
  if (error.status === 401 || error.code === 'INVALID_CREDENTIALS') {
    return 'That current password is not correct.'
  }
  if (error.status === 0) return 'Cannot reach the server. Check your connection.'
  return 'Could not change your password. Please try again.'
}
